import type { PredictorConfig } from "../engine/config";
import type { IsoDateTime } from "../engine/dates";
import { learn } from "../engine/offsetLearning";
import type { Repositories } from "../storage/repositories";
import { dateOf } from "../engine/dates";
import { effectiveGrinder } from "./slopeService";
import { dialedBeans, referenceFor } from "./startingPointService";

/**
 * Runs the learning rule once per (bean, grinder) pair that has dialed in and has not been learned yet.
 * Called on launch; idempotent because every learned pair is recorded in the bean_learning ledger.
 *
 * x_ref for the pair is computed from the OTHER beans on that grinder as of the bean's last shot, so a bean never
 * learns against a reference that already includes itself.
 */
export interface LearnedPair {
  readonly beanId: number;
  readonly grinderId: number;
  readonly deltaRealised: number;
  readonly residual: number;
  readonly attributesUpdated: number;
}

export async function runLearning(repos: Repositories, cfg: PredictorConfig, now: IsoDateTime): Promise<LearnedPair[]> {
  const learned: LearnedPair[] = [];
  for (const seeded of await repos.grinders.findAll()) {
    if (seeded.id === null) continue;
    // the learned grinder slope stands in for the seed, as everywhere else
    const grinder = await effectiveGrinder(repos, seeded, dateOf(now), cfg);
    if (grinder.id === null) continue;
    for (const d of await dialedBeans(repos, grinder, cfg)) {
      const beanId = d.bean.id as number;
      if (d.validShots < cfg.learningMinValidShots) continue;
      if (await repos.learning.isLearned(beanId, grinder.id)) continue;

      const ref = await referenceFor(repos, grinder, d.lastShotDate, cfg, beanId);
      const offsets = await repos.offsets.load();
      const u = learn(d.bean, d.bFinal, d.xDialed, ref.xRef, offsets, cfg);
      if (u.attributesUpdated > 0) await repos.offsets.save(u.offsets);
      await repos.learning.markLearned(beanId, grinder.id, now);
      learned.push({ beanId, grinderId: grinder.id, deltaRealised: u.deltaRealised, residual: u.residual, attributesUpdated: u.attributesUpdated });
    }
  }
  return learned;
}
